import { max, cloneDeep } from 'lodash'
import {
  HourSerieCalculatorResult,
  HourSeriesResult,
  InputHourSerieType,
} from '../building-energy-model/hour-series-calculator'
import {
  BemCorrectionFactorsData,
  BuildingEnergyModelFragment,
  CoolingParameters,
  EnergySystemType,
} from '../generated/graphql'
import { hoursInYear } from '../util/hours-in-year'
import { coolingCalculator } from './cooling-hour-series-calculator'
import { dhwCalculator } from './dhw-hour-series-calculator'
import { electricityConsumptionCalculator } from './electricity-consumption-hour-series-calculator'
import { heatLoadCalculator } from './heatload-hour-series-calculator'
import { spaceHeatingCalculator } from './space-heating-hour-series-calculator'
import { ventilationCalculator } from './ventilation-hour-series-calculator'

export function calculateHourSeries(
  model: BuildingEnergyModelFragment,
  outsideTempHourSerie: number[],
  year: number,
): HourSeriesResult[] {
  const result: HourSeriesResult[] = []

  let ventilationResult: HourSerieCalculatorResult = { outputs: [], inputs: [] }

  // If ventilation system exists, calculate it first
  const ventilation = model.energySystems.find(
    (system) => system.type === EnergySystemType.Ventilation,
  )
  if (ventilation) {
    // Input from cooling system
    let coolingDemandDisplacementConstant = 0
    const cooling = model.energySystems.find(
      (system) => system.type === EnergySystemType.Cooling,
    )
    if (cooling)
      coolingDemandDisplacementConstant = (
        cooling.parameters as CoolingParameters
      ).acDemandDisplacementConstant

    ventilationResult = ventilationCalculator(
      model.schedules,
      outsideTempHourSerie,
      ventilation,
      coolingDemandDisplacementConstant,
      year,
    )
  }

  result.push(...ventilationResult.outputs)

  let heatloadResult: HourSerieCalculatorResult = { outputs: [], inputs: [] }

  // Next one is heat loads system
  const heatLoads = model.energySystems.find(
    (system) => system.type === EnergySystemType.HeatLoad,
  )
  if (heatLoads)
    heatloadResult = heatLoadCalculator(
      model.schedules,
      outsideTempHourSerie,
      heatLoads,
      model.area,
      year,
    )

  // Next one is space heating system
  let spaceHeatingResult: HourSerieCalculatorResult = {
    outputs: [],
    inputs: [],
  }

  const spaceHeating = model.energySystems.find(
    (system) => system.type === EnergySystemType.SpaceHeating,
  )
  if (spaceHeating)
    spaceHeatingResult = spaceHeatingCalculator(
      outsideTempHourSerie,
      ventilationResult.inputs.find(
        (r) => r.inputSerieType === InputHourSerieType.VentilationHeatingDemand,
      )?.hourSerie ?? (Array.from(Array(hoursInYear).fill(0)) as number[]),
      heatloadResult.inputs.find(
        (r) => r.inputSerieType === InputHourSerieType.HeatLoadHeatloadSupply,
      )?.hourSerie ?? (Array.from(Array(hoursInYear).fill(0)) as number[]),
      spaceHeating,
      year,
    )

  result.push(...spaceHeatingResult.outputs)

  // Next one is electricity consumption system
  let elConsumtionResult: HourSerieCalculatorResult = {
    outputs: [],
    inputs: [],
  }

  const electricity = model.energySystems.find(
    (system) => system.type === EnergySystemType.ElectricityConsumption,
  )
  if (electricity)
    elConsumtionResult = electricityConsumptionCalculator(
      ventilationResult.inputs.find(
        (r) =>
          r.inputSerieType === InputHourSerieType.VentilationElectricityDemand,
      )?.hourSerie ?? (Array.from(Array(hoursInYear).fill(0)) as number[]),
      heatloadResult.inputs.find(
        (r) =>
          r.inputSerieType ===
          InputHourSerieType.HeatLoadAppliancesElectricityDemand,
      )?.hourSerie ?? (Array.from(Array(hoursInYear).fill(0)) as number[]),
      heatloadResult.inputs.find(
        (r) =>
          r.inputSerieType ===
          InputHourSerieType.HeatLoadLightingElectricityDemand,
      )?.hourSerie ?? (Array.from(Array(hoursInYear).fill(0)) as number[]),
      electricity,
      year,
    )

  result.push(...elConsumtionResult.outputs)

  for (let i = 0; i < model.energySystems.length; i++) {
    const energySystem = model.energySystems[i]

    switch (energySystem.type) {
      case EnergySystemType.DomesticHotWater:
        result.push(...dhwCalculator(energySystem, model.area, year))
        break
      case EnergySystemType.Cooling:
        result.push(
          ...coolingCalculator(
            outsideTempHourSerie,
            ventilationResult.inputs.find(
              (r) =>
                r.inputSerieType ===
                InputHourSerieType.VentilationCoolingDemand,
            )?.hourSerie ??
              (Array.from(Array(hoursInYear).fill(0)) as number[]),
            energySystem,
            model.area,
            year,
          ),
        )
        break

      default:
        break
    }
  }

  return result
}

export const calculateHourSerieWithFactors = (
  hourSerie: HourSeriesResult,
  factorsData: BemCorrectionFactorsData,
): HourSeriesResult => {
  const resultSerie = cloneDeep(hourSerie)

  const maxValue =
    (max(resultSerie.hourSerie) as number) * factorsData.correctionFactor

  if (maxValue === 0)
    return { ...resultSerie, hourSerie: Array<number>(hoursInYear).fill(0) }

  for (let i = 0; i < resultSerie.hourSerie.length; i++) {
    resultSerie.hourSerie[i] *= factorsData.correctionFactor
    const ratio = resultSerie.hourSerie[i] / maxValue

    // Prevent negative base with fractional exponent
    if (ratio > 0)
      resultSerie.hourSerie[i] *= ratio ** (1 / factorsData.energyFactor - 1)
    else resultSerie.hourSerie[i] = 0
  }

  return resultSerie
}

export const calculateHourSeriesWithFactors = (
  model: BuildingEnergyModelFragment,
  outsideTempHourSerie: number[],
  year: number,
): HourSeriesResult[] => {
  const resultSeries: HourSeriesResult[] = []

  const seriesWithoutFactors = calculateHourSeries(
    model,
    outsideTempHourSerie,
    year,
  )

  const { correctionFactors } = model

  if (!correctionFactors) return seriesWithoutFactors

  seriesWithoutFactors.forEach((serie) => {
    const factor = correctionFactors.find(
      (f) => f.energyType === serie.consumptionEnergyType,
    )
    if (factor) {
      const tempSerie = calculateHourSerieWithFactors(serie, factor)

      resultSeries.push(tempSerie)
    }
  })

  return resultSeries
}
